import React, { useEffect, useState } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { Typography, Button, Space } from 'antd';
import { Link, useSearchParams } from 'react-router-dom';
import {
    SalesReport,
    InventoryReport,
    CustomerSalesReport,
    PurchaseReport,
    ShiftCloseoutReport
} from './index';
import {
    ShoppingOutlined,
    ShoppingCartOutlined,
    UserOutlined,
    AppstoreOutlined,
    DollarOutlined
} from '@ant-design/icons';

const { Title } = Typography;

const StyledTrigger = ({ children, value, active }: { children: React.ReactNode, value: string, active: string }) => (
    <TabsPrimitive.Trigger
        value={value}
        style={{
            padding: '10px 16px',
            border: 'none',
            borderBottom: active === value ? '2px solid #1890ff' : '2px solid transparent',
            background: 'none',
            fontSize: '14px',
            fontWeight: active === value ? 'bold' : 'normal',
            color: active === value ? '#1890ff' : 'rgba(0, 0, 0, 0.65)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            marginRight: '24px',
        }}
    >
        {children}
    </TabsPrimitive.Trigger>
);

const VALID_TABS = new Set(['sales', 'inventory', 'customer', 'purchase', 'shift']);

export const Reports: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab') ?? '';
    const initialTab = VALID_TABS.has(tabParam) ? tabParam : 'sales';
    const [activeTab, setActiveTab] = useState(initialTab);

    useEffect(() => {
        const t = searchParams.get('tab') ?? '';
        if (VALID_TABS.has(t)) {
            setActiveTab(t);
        }
    }, [searchParams]);

    const handleTabChange = (value: string) => {
        setActiveTab(value);
        setSearchParams({ tab: value }, { replace: true });
    };

    return (
        <div style={{ padding: '20px' }}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Title level={2} style={{ marginBottom: 0 }}>Reports Dashboard</Title>
                <div style={{ marginBottom: 8 }}>
                    <Link to="/financial-reports">
                        <Button type="link" style={{ paddingLeft: 0 }}>
                            Open ledger profit and loss (financial reports)
                        </Button>
                    </Link>
                </div>
            </Space>

            <TabsPrimitive.Root
                value={activeTab}
                onValueChange={handleTabChange}
                style={{ marginTop: '20px' }}
            >
                <TabsPrimitive.List
                    style={{
                        display: 'flex',
                        borderBottom: '1px solid #f0f0f0',
                        marginBottom: '16px'
                    }}
                >
                    <StyledTrigger value="sales" active={activeTab}>
                        <ShoppingOutlined style={{ marginRight: '8px' }} />
                        Sales Report
                    </StyledTrigger>

                    <StyledTrigger value="inventory" active={activeTab}>
                        <AppstoreOutlined style={{ marginRight: '8px' }} />
                        Inventory Report
                    </StyledTrigger>

                    <StyledTrigger value="customer" active={activeTab}>
                        <UserOutlined style={{ marginRight: '8px' }} />
                        Customer Report
                    </StyledTrigger>

                    <StyledTrigger value="purchase" active={activeTab}>
                        <ShoppingCartOutlined style={{ marginRight: '8px' }} />
                        Purchase Report
                    </StyledTrigger>

                    <StyledTrigger value="shift" active={activeTab}>
                        <DollarOutlined style={{ marginRight: '8px' }} />
                        Shift Close-out
                    </StyledTrigger>
                </TabsPrimitive.List>

                <TabsPrimitive.Content value="sales">
                    <SalesReport />
                </TabsPrimitive.Content>

                <TabsPrimitive.Content value="inventory">
                    <InventoryReport />
                </TabsPrimitive.Content>

                <TabsPrimitive.Content value="customer">
                    <CustomerSalesReport />
                </TabsPrimitive.Content>

                <TabsPrimitive.Content value="purchase">
                    <PurchaseReport />
                </TabsPrimitive.Content>

                <TabsPrimitive.Content value="shift">
                    <ShiftCloseoutReport />
                </TabsPrimitive.Content>
            </TabsPrimitive.Root>
        </div>
    );
}; 